
import {
  Vue, Component, Prop,
} from 'vue-property-decorator';
import {
  Lead, User, PermissionCode, Checklist, ChildLeads, Task,
} from '@/types';
import LeadCancelDialog from '@/components/LeadCancelDialog.vue';
import LeadRejectDialog from '@/components/LeadRejectDialog.vue';
import LeadTakeDialog from '@/components/LeadTakeDialog.vue';
import LeadReassignDialog from '@/components/LeadReassignDialog.vue';
import LeadCloseDialog from '@/components/LeadCloseDialog.vue';
import LeadResolveDialog from '@/components/LeadResolveDialog.vue';
import LeadReopenDialog from '@/components/LeadReopenDialog.vue';
import LeadSuperActions from '@/components/LeadSuperActions.vue';
import { currentUserHasPermission, currentUserIsRequester, currentUserIsSuper } from '@/helpers';

@Component({
  components: {
    LeadCancelDialog,
    LeadRejectDialog,
    LeadTakeDialog,
    LeadReassignDialog,
    LeadCloseDialog,
    LeadResolveDialog,
    LeadReopenDialog,
    LeadSuperActions,
  },
})
export default class LeadActions extends Vue {
  @Prop(Boolean) readonly loading!: boolean;
  @Prop({
    type: Boolean,
    default: true,
  }) readonly back!: boolean;

  cancelDialog = false
  rejectDialog = false
  takeDialog = false
  reassignDialog = false
  closeDialog = false
  resolveDialog = false
  reopenDialog = false

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get childLeads(): ChildLeads {
    return this.$store.getters.currentChildren;
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get isSuper(): boolean { return currentUserIsSuper(); }

  get isRequester(): boolean { return currentUserIsRequester(); }

  get tasksWithErrors(): boolean {
    if (this.lead && 'Tasks' in this.lead) {
      // eslint-disable-next-line no-restricted-syntax
      for (const task of this.lead.Tasks) {
        if ('status' in task && task.status === 'error') {
          return true;
        }
      }
    }

    return false;
  }

  get version() {
    return this.lead && 'version' in this.lead && this.lead.version ? Number(this.lead.version) : null;
  }

  get hasOpenChildren(): boolean {
    // Le lead figlie non possono avere altre figlie, quindi restituiscono sempre false
    if (['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.lead.type)) {
      return false;
    }

    // Esclude la lead corrente dal controllo delle figlie aperte
    const otherChildren = Object.values(this.childLeads).filter((l) => l.id !== this.lead.id);
    return otherChildren.some((l) => l.status === 'open');
  }

  get cStatus(): 'error' | 'disabled' | 'completed' | null {
    const task: Task | null = this.lead && 'Tasks' in this.lead
      ? this.lead.Tasks.find((t) => t.name === 'C') || null
      : null;

    return task?.status ?? null;
  }

  get pStatus(): 'error' | 'disabled' | 'completed' | null {
    const task: Task | null = this.lead && 'Tasks' in this.lead
      ? this.lead.Tasks.find((t) => t.name === 'P') || null
      : null;

    return task?.status ?? null;
  }

  get isResolveVisibile(): boolean {
    if (!this.lead
      || 'status' in this.lead === false
      || this.lead.status !== 'open') {
      return false;
    }

    return this.hasPermission(`leads_${this.lead.type}.resolve`);
  }

  get isReopenVisibile(): boolean {
    if (!this.lead
      || 'status' in this.lead === false
      || (
        this.lead.status !== 'closed'
        && this.lead.status !== 'resolved'
        && this.lead.status !== 'rejected'
      )) {
      return false;
    }

    return this.hasPermission('leads.reopen');
  }

  get isRejectVisible() {
    return this.lead
      && 'status' in this.lead
      && this.lead.status === 'new'
      && !this.isRequester
      && this.hasPermission(`leads_${this.lead.type}.reject`);
  }

  get isCancelVisible() {
    if (!this.lead || 'status' in this.lead === false) return false;

    return (this.isRequester || this.isSuper)
      && this.lead.status === 'new'
      && (
        this.hasPermission(`leads_${this.lead.type}.cancel`)
        || (this.lead.type === 'richiesta' && this.hasPermission('leads_richiesta_appuntamento.cancel'))
      );
  }

  get isTakeVisible(): boolean {
    if (!this.lead || 'status' in this.lead === false
      || this.lead.status !== 'new') return false;

    if (this.isRequester && !this.lead.owners.find((u) => u.id === this.user.id)) return false;

    return this.hasPermission(`leads_${this.lead.type}.take`);
  }

  // Mostra il pulsante "Riassegna" al richiedente se la lead è stata
  // annullata o respinta.
  get isReassignVisible() {
    if (!this.lead || 'status' in this.lead === false) return false;

    return (this.isRequester || this.isSuper)
      && (this.lead.status === 'rejected' || this.lead.status === 'cancelled');
  }

  get isCloseVisible(): boolean {
    if (!this.lead || 'status' in this.lead === false) return false;

    // Se l'utente è un SuperUtente e lo stato della lead è Aperto o Respinto,
    // mostra il tasto Chiudi.
    if (this.isSuper
      && (['open', 'rejected'].includes(this.lead.status))) return true;

    // Se l'utente (non SuperUser) non ha il permesso globale di chiusura e
    // nemmeno quello specifico per i task P o C rossi,
    // nasconde il tasto Chiudi.
    if (!this.hasPermission(`leads_${this.lead.type}.close`)
      && !this.hasPermission('leads.pc_close')) return false;

    if (this.hasPermission('leads.pc_close')
      && (
        this.hasOpenChildren
        || (this.cStatus !== 'error' && this.pStatus !== 'error')
      )) return false;

    // Se l'utente è il richiedente e lo stato è Respinta,
    // può chiudere.
    if (this.isRequester && this.lead.status === 'rejected') {
      return true;
    }

    // Se la lead è in lavorazione e ci sono task con errori,
    // può chiudere.
    return this.lead.status === 'open' && this.tasksWithErrors;
  }

  get isReviewVisibile(): boolean {
    const aTask = this.getTask('A');

    return aTask !== null
      && (aTask.status === 'error' || (aTask.status === 'completed' && aTask.extraCosti === true))
      && this.hasPermission(`leads_${this.lead.type}.review`);
  }

  get checklists(): Checklist[] {
    return this.lead && Array.isArray(this.lead.checklists) && this.lead.checklists.length > 0
      ? this.lead.checklists
      : [];
  }

  get checklistsComplete() {
    if (this.checklists.length > 0) {
    // eslint-disable-next-line no-restricted-syntax
      for (const checklist of this.checklists) {
        // eslint-disable-next-line no-restricted-syntax
        for (const task of checklist.tasks) {
          if (task.status !== 'completed') {
            return false;
          }
        }
      }
    }

    return true;
  }

  // Tutti i Task (esclusi quelli delle figlie) sono stati completati?
  get tasksCompleted() {
    if (this.lead && 'Tasks' in this.lead) {
      // eslint-disable-next-line no-restricted-syntax
      for (const task of this.lead.Tasks.filter((t) => t.status !== 'disabled' && t.relationship !== 'father')) {
        if (task.status !== 'completed') {
          return false;
        }
      }

      return true;
    }

    return false;
  }

  // La Lead è completabile?
  get solvable() {
    // Se l'eventuale Checklist non è completa,
    // la Lead non è completabile.
    if (!this.checklistsComplete) return false;

    // Se c'è una lead figlia ma è chiusa, la lead non è completabile
    let closedFound = false;
    if (this.childLeads) {
      Object.values(this.childLeads).forEach((cl) => {
        if (cl && cl.status === 'closed') closedFound = true;
      });
      if (closedFound) return false;
    }

    // Se i Task del padre sono completi e non sono presenti figlie aperte,
    // la Lead è completabile.
    return this.tasksCompleted && !this.hasOpenChildren;
  }

  hasPermission(codename: PermissionCode): boolean { return currentUserHasPermission(codename); }

  getTask(name) {
    if (this.lead && 'Tasks' in this.lead) {
      // eslint-disable-next-line no-restricted-syntax
      for (const task of this.lead.Tasks) {
        if (task.name === name) {
          return task;
        }
      }
    }

    return null;
  }

  onCancel() {
    console.log('LeadActions: onCancel()'); // eslint-disable-line no-console

    // Torna all'elenco
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    this.$router.replace({ name: 'Elenco' }, () => {});
  }

  onResolve() {
    console.log('LeadActions: onResolve()'); // eslint-disable-line no-console

    this.resolveDialog = true;
  }

  onReopen() {
    console.log('LeadActions: onReopen()'); // eslint-disable-line no-console

    this.reopenDialog = true;
  }

  onError() {
    console.log('LeadActions: onError()'); // eslint-disable-line no-console

    this.$emit('error');
  }

  onReview() {
    console.log('LeadActions: onReview()'); // eslint-disable-line no-console
  }

  onCancelLead() {
    console.log('LeadActions: onCancelLead()'); // eslint-disable-line no-console

    this.cancelDialog = true;
  }

  onReject() {
    console.log('LeadActions: onReject()'); // eslint-disable-line no-console

    this.rejectDialog = true;
  }

  onTake() {
    console.log('LeadActions: onTake()'); // eslint-disable-line no-console

    this.takeDialog = true;
  }

  onReassign() {
    console.log('LeadActions: onReassign()'); // eslint-disable-line no-console

    this.reassignDialog = true;
  }

  onClose() {
    console.log('LeadActions: onClose()'); // eslint-disable-line no-console

    this.closeDialog = true;
  }
}
